import { useEffect, useRef, useState } from "react";
import { type ShowData, type ShowMeta } from "../showFormat";
import { showBuffers } from "../showBuffers";
import { SPEEDS, fmt } from "../util";

function pickSpeed(dur: number) {
  return SPEEDS.find((s) => dur / s <= 130) ?? 16;
}

/** Parse a show .zip in a Web Worker so the main thread never hangs. */
function parseInWorker(ab: ArrayBuffer, name: string): Promise<ShowData> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL("../showFormat.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent) => {
      worker.terminate();
      if (e.data?.ok) resolve(e.data.data as ShowData);
      else reject(new Error(e.data?.error || "Could not parse that show."));
    };
    worker.onerror = (ev) => {
      worker.terminate();
      reject(new Error(ev.message || "Show parser worker failed."));
    };
    worker.postMessage({ ab, name }, [ab]);
  });
}

/**
 * Owns real-show playback: loading a .zip, the transport (play/speed/scrub),
 * and the current-time readout. `onError` surfaces failures.
 */
export function useShowLoader(onError: (msg: string) => void) {
  // Only the lightweight metadata lives in React state; the heavy position/
  // colour buffers go to the off-React `showBuffers` store (ShowPlayer reads
  // them there). This keeps megabytes out of every component's props.
  const [show, setShow] = useState<ShowMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(4);
  const [nowLabel, setNowLabel] = useState("0:00");
  const playhead = useRef(0);
  const sliderRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!show) return;
    const id = window.setInterval(() => setNowLabel(fmt(playhead.current)), 150);
    return () => window.clearInterval(id);
  }, [show]);

  async function loadBuffer(ab: ArrayBuffer, name: string) {
    setLoading(true);
    try {
      const data = await parseInWorker(ab, name); // off-thread — UI stays responsive
      const { posScene, colRGB, ...meta } = data;
      showBuffers.set({ posScene, colRGB }); // heavy buffers off-React; set before publishing meta
      playhead.current = 0;
      setSpeed(pickSpeed(meta.durationSec));
      setPlaying(true);
      setShow(meta);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadSample() {
    setLoading(true);
    try {
      const res = await fetch("/shows/io-2026-mgp.zip");
      await loadBuffer(await res.arrayBuffer(), "MultiGP IO 2026");
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
      setLoading(false);
    }
  }

  function onShowFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) f.arrayBuffer().then((ab) => loadBuffer(ab, f.name.replace(/\.zip$/i, "")));
    e.target.value = "";
  }

  /** Close the current show (back to studio): drop the buffers, then the meta. */
  function closeShow() {
    showBuffers.set(null);
    setShow(null);
  }

  return {
    show, closeShow, loading, playing, setPlaying, speed, setSpeed,
    nowLabel, playhead, sliderRef, loadSample, onShowFile,
  };
}
